'use client';

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

type StudioPreviewPortalContextValue = {
  portalRoot: HTMLElement | null;
  scrollRoot: HTMLElement | null;
  setPortalRoot: (el: HTMLElement | null) => void;
  setScrollRoot: (el: HTMLElement | null) => void;
};

const StudioPreviewPortalContext = createContext<StudioPreviewPortalContextValue | null>(null);

export function StudioPreviewPortalProvider({ children }: { children: ReactNode }) {
  const [portalRoot, setPortalRootState] = useState<HTMLElement | null>(null);
  const [scrollRoot, setScrollRootState] = useState<HTMLElement | null>(null);
  const setPortalRoot = useCallback((el: HTMLElement | null) => {
    setPortalRootState(el);
  }, []);
  const setScrollRoot = useCallback((el: HTMLElement | null) => {
    setScrollRootState(el);
  }, []);

  const value = useMemo(
    () => ({ portalRoot, scrollRoot, setPortalRoot, setScrollRoot }),
    [portalRoot, scrollRoot, setPortalRoot, setScrollRoot],
  );

  return (
    <StudioPreviewPortalContext.Provider value={value}>
      {children}
    </StudioPreviewPortalContext.Provider>
  );
}

export function useStudioPreviewPortalRoot() {
  return useContext(StudioPreviewPortalContext)?.portalRoot ?? null;
}

export function useStudioPreviewPortalRootSetter() {
  return useContext(StudioPreviewPortalContext)?.setPortalRoot;
}

export function useStudioPreviewScrollRoot() {
  return useContext(StudioPreviewPortalContext)?.scrollRoot ?? null;
}

export function useStudioPreviewScrollRootSetter() {
  return useContext(StudioPreviewPortalContext)?.setScrollRoot;
}

/** Locks the studio preview scroll container while an overlay is open. */
export function useStudioPreviewScrollLock(active: boolean) {
  const scrollRoot = useStudioPreviewScrollRoot();

  useEffect(() => {
    if (!active || !scrollRoot) return;

    const previousOverflow = scrollRoot.style.overflow;
    scrollRoot.style.overflow = 'hidden';

    return () => {
      scrollRoot.style.overflow = previousOverflow;
    };
  }, [active, scrollRoot]);
}
